import {
  format as formatFns,
  isAfter as isAfterFns,
  isBefore as isBeforeFns,
  isSameDay as isSameDayFns,
  isEqual as isEqualFns,
  isToday as isTodayFns,
  isTomorrow as isTomrorowFns,
  isYesterday as isYesterdayFns,
  isSameMonth as isSameMonthFns,
  isSameYear as isSameYearFns,
  isValid as isValidFns,
  addBusinessDays as addBusinessDaysFns,
  add as addFns,
  addDays as addDaysFns,
  sub as subFns,
  millisecondsToMinutes as millisecondsToMinutesFns,
  subBusinessDays as subBusinessDaysFns,
  differenceInMilliseconds as differenceInMillisecondsFns,
  differenceInMinutes as differenceInMinutesFns,
  differenceInHours as differenceInHoursFns,
  differenceInDays as differenceInDaysFns,
  differenceInWeeks as differenceInWeeksFns,
  differenceInMonths as differenceInMonthsFns,
  differenceInYears as differenceInYearsFns,
  differenceInCalendarDays as differenceInCalendarDaysFns,
  eachDayOfInterval as eachDayOfIntervalFns,
  eachWeekendOfInterval as eachWeekendOfIntervalFns,
  startOfHour as startOfHourFns,
  startOfDay as startOfDayFns,
  startOfMonth as startOfMonthFns,
  endOfDay as endOfDayFns,
  startOfWeek as startOfWeekFns,
  startOfYear as startOfYearFns,
  startOfISOWeek as startOfISOWeekFns,
  endOfHour as endOfHourFns,
  endOfWeek as endOfWeekFns,
  endOfMonth as endOfMonthFns,
  endOfYear as endOfYearFns,
  set as setFns,
  getMinutes as getMinutesFns,
  isPast as isPastFns,
  isFuture as isFutureFns,
  isWithinInterval as isWithinIntervalFns,
  intervalToDuration as intervalToDurationFns,
  Duration,
  Interval,
  parse as parseFns,
  getDay as getDayFns,
  previousDay as previousDayFns,
  areIntervalsOverlapping as areIntervalsOverlappingFns,
  formatISODuration as formatISODurationFns,
  formatISO as formatISOFns,
  parseISO as parseISOFns,
  formatRelative as formatRelativeFns,
  getWeek as getWeekFns,
  getMonth as getMonthFns,
  nextMonday as nextMondayFns,
  isSunday as isSundayFns,
  isWeekend as isWeekendFns,
  getHours as getHoursFns,
  getDate as getDateFns,
  ParseOptions as ParseOptionsFns,
  FormatOptions as FormatOptionsFns,
  Day,
} from 'date-fns'
import { getDateFnsLocale } from './locales'

type FormatOptions = Omit<FormatOptionsFns, 'locale'> & { locale?: string }
type ParseOptions = Omit<ParseOptionsFns, 'locale'> & { locale?: string }

type Unit = 'day' | 'week' | 'month' | 'year'

type DateOrNumber = Date | number

export const format = (date: Date | number, format: string, options?: FormatOptions) => {
  const dateFnsLocale = getDateFnsLocale(options?.locale)
  return formatFns(date, format, { ...options, locale: dateFnsLocale })
}

/**
 * Formats a date based on another date
 *
 * @param {Date | number} date - Date to be formatted
 * @param {Date | number} baseDate - Base date to be compared - usually the current date
 * @param {{
 *   locale?: string
 *   weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6
 * }} options - [Optional]:
 *   Options to customize formatting
 * @returns {string} - The date formatted in past like "Last week|month", "{number} months|weeks
 *   ago", "Yesterday", "Earlier today"
 */
export const formatRelative = (
  date: Date | number,
  baseDate: Date | number,
  options?: {
    locale?: string
    weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6
  }
) => {
  const diffInMonths = differenceInMonths(baseDate, date)
  if (diffInMonths > 1) {
    return `${diffInMonths} months ago`
  }

  const isLastMonth = getMonthFns(baseDate) === getMonthFns(date) + 1

  if (isLastMonth) {
    return 'Last month'
  }

  const diffInWeeks = differenceInWeeks(baseDate, date)
  if (diffInWeeks > 1) {
    return `${diffInWeeks} weeks ago`
  }

  // fixes bug on date-fns where it no longer considers last
  // week after 7 days have passed
  // (monday 7:30 vs next week moday 7:31 is no longer last week)
  const isLastWeek = getWeekFns(baseDate) === getWeekFns(date) + 1

  if (isLastWeek) {
    return 'Last week'
  }

  const formatRelativeLocale = {
    lastWeek: "'Last week'",
    yesterday: "'Yesterday'",
    today: "'Earlier today'",
    tomorrow: "'tomorrow at' p",
    nextWeek: "eeee 'at' p",
    other: 'MM/dd/yyyy',
  }
  const locale = {
    ...getDateFnsLocale(options?.locale),
    formatRelative: (token: keyof typeof formatRelativeLocale) => formatRelativeLocale[token],
  }
  return formatRelativeFns(date, baseDate, {
    ...options,
    locale,
  })
}

export const millisecondsToMinutes = (milliseconds: number) => {
  return millisecondsToMinutesFns(milliseconds)
}

export const startOfISOWeek = (date: Date | number) => {
  return startOfISOWeekFns(date)
}

export const startOfHour = (date: Date) => startOfHourFns(date)

export const startOfDay = (date: Date) => {
  return startOfDayFns(date)
}

export const startOfMonth = (date: Date) => {
  return startOfMonthFns(date)
}

export const endOfDay = (date: Date) => {
  return endOfDayFns(date)
}

export const startOfWeek = (
  date: Date,
  options?: Pick<FormatOptions, 'locale' | 'weekStartsOn'>
) => {
  const dateFnsLocale = getDateFnsLocale(options?.locale)

  return startOfWeekFns(date, { ...options, locale: dateFnsLocale })
}

export const startOfYear = (date: Date) => {
  return startOfYearFns(date)
}

export const endOfHour = (date: Date) => {
  return endOfHourFns(date)
}

export const endOfWeek = (date: Date, options?: Pick<FormatOptions, 'locale' | 'weekStartsOn'>) => {
  const dateFnsLocale = getDateFnsLocale(options?.locale)

  return endOfWeekFns(date, { ...options, locale: dateFnsLocale })
}

export const endOfMonth = (date: Date) => {
  return endOfMonthFns(date)
}

export const endOfYear = (date: Date) => {
  return endOfYearFns(date)
}

export const startOf = (date: Date, unit: Unit) => {
  switch (unit) {
    case 'week':
      return startOfWeek(date)
    case 'month':
      return startOfMonth(date)
    case 'year':
      return startOfYear(date)
    default:
      return startOfDay(date)
  }
}

export const endOf = (date: Date, unit: Unit) => {
  switch (unit) {
    case 'week':
      return endOfWeek(date)
    case 'month':
      return endOfMonth(date)
    case 'year':
      return endOfYear(date)
    default:
      return endOfDay(date)
  }
}

export const subBusinessDays = (date: Date, amount: number) => {
  return subBusinessDaysFns(date, amount)
}

export const subtract = (date: Date, duration: Duration) => {
  return subFns(date, duration)
}

export const add = (date: Date, duration: Duration) => {
  return addFns(date, duration)
}

export const addDays = (date: Date | number, amount: number) => {
  return addDaysFns(date, amount)
}

export const differenceInMilliseconds = (date: Date | number, dateToCompare: Date | number) => {
  return differenceInMillisecondsFns(date, dateToCompare)
}

export const differenceInMinutes = (date: Date | number, dateToCompare: Date | number) => {
  return differenceInMinutesFns(date, dateToCompare)
}

export const differenceInHours = (date: Date | number, dateToCompare: Date | number) => {
  return differenceInHoursFns(date, dateToCompare)
}

export const differenceInDays = (date: Date | number, dateToCompare: Date | number) => {
  return differenceInDaysFns(date, dateToCompare)
}

export const differenceInWeeks = (date: Date | number, dateToCompare: Date | number) => {
  return differenceInWeeksFns(date, dateToCompare)
}

export const differenceInMonths = (date: Date | number, dateToCompare: Date | number) => {
  return differenceInMonthsFns(date, dateToCompare)
}
export const differenceInYears = (date: Date | number, dateToCompare: Date | number) => {
  return differenceInYearsFns(date, dateToCompare)
}

export const differenceInCalendarDays = (date: number | Date, dateToCompare: number | Date) => {
  return differenceInCalendarDaysFns(date, dateToCompare)
}

export const addBusinessDays = (date: Date, amount: number) => {
  return addBusinessDaysFns(date, amount)
}

export const isValid = (date: unknown) => {
  return isValidFns(date)
}

export const isPast = (date: Date | number) => {
  return isPastFns(date)
}

export const isWithinInterval = (date: Date | number, interval: Interval) => {
  return isWithinIntervalFns(date, interval)
}

export const isFuture = (date: Date | number) => {
  return isFutureFns(date)
}

export const isToday = (date: Date | number) => {
  return isTodayFns(date)
}

export const isTomorrow = (date: Date | number) => {
  return isTomrorowFns(date)
}

export const isYesterday = (date: Date | number) => {
  return isYesterdayFns(date)
}

export const isSameMonth = (date: Date | number, dateToCompare: Date | number) => {
  return isSameMonthFns(date, dateToCompare)
}

export const isSameYear = (date: Date | number, dateToCompare: Date | number) => {
  return isSameYearFns(date, dateToCompare)
}

export const isBefore = (date: Date | number, dateToCompare: Date | number) => {
  return isBeforeFns(date, dateToCompare)
}

export const isAfter = (date: Date | number, dateToCompare: Date | number) => {
  return isAfterFns(date, dateToCompare)
}

export const isSameDay = (date: Date | number, dateToCompare: Date | number) => {
  return isSameDayFns(date, dateToCompare)
}
export const isEqual = (date: Date | number, dateToCompare: Date | number) => {
  return isEqualFns(date, dateToCompare)
}

export const eachDayOfInterval = (interval: Interval) => {
  return eachDayOfIntervalFns(interval)
}

export const eachWeekendOfInterval = (interval: Interval) => {
  return eachWeekendOfIntervalFns(interval)
}

export const set = (date: Date | number, options: Parameters<typeof setFns>[1]) => {
  return setFns(date, options)
}

export const getMinutes = (date: DateOrNumber) => {
  return getMinutesFns(date)
}

export const intervalToDuration = (interval: Interval) => {
  return intervalToDurationFns(interval)
}

export const parse = (
  dateString: string,
  formatString: string,
  referenceDate: Date | number,
  options?: ParseOptions
) => {
  const dateFnsLocale = getDateFnsLocale(options?.locale)
  return parseFns(dateString, formatString, referenceDate, { ...options, locale: dateFnsLocale })
}

export const getDay = (date: Date | number) => {
  return getDayFns(date)
}
export const previousDay = (date: Date | number, day: Day) => {
  return previousDayFns(date, day)
}
export const areIntervalsOverlapping = (
  intervalLeft: Interval,
  intervalRight: Interval,
  options?: {
    inclusive?: boolean
  }
) => {
  return areIntervalsOverlappingFns(intervalLeft, intervalRight, options)
}

export const parseDateToISOFormat = (
  date: Date,
  options?: {
    format?: 'extended' | 'basic'
    representation?: 'complete' | 'date' | 'time'
  }
) => {
  return formatISOFns(date, options)
}

export const parseISOToDate = (isoDate: string) => {
  return parseISOFns(isoDate)
}

export const formatDurationToISO = (duration: Duration) => {
  return formatISODurationFns(duration)
}

export const formatDurationToISOTime = (duration: Duration) => {
  return `PT${formatISODurationFns(duration).split('T')[1]}`
}

// This is copied from date-fns open PR: https://github.com/date-fns/date-fns/pull/3151
// Maybe next version will have this function, after that replace this with the function from the lib
export const parseISODuration = (argument: string): Required<Duration> => {
  const ISO_DURATION_REGEX =
    /P(?:([\d]+\.?[\d]*|\.[\d]+)Y)?(?:([\d]+\.?[\d]*|\.[\d]+)M)?(?:([\d]+\.?[\d]*|\.[\d]+)W)?(?:([\d]+\.?[\d]*|\.[\d]+)D)?(?:T(?:([\d]+\.?[\d]*|\.[\d]+)H)?(?:([\d]+\.?[\d]*|\.[\d]+)M)?(?:([\d]+\.?[\d]*|\.[\d]+)S)?)?$/
  const parsedArgument = argument.replace(/,/g, '.') // Decimal fraction may be specified with either a comma or a full stop

  const match = ISO_DURATION_REGEX.exec(parsedArgument)

  if (!match) {
    throw new RangeError('Invalid format')
  }

  const [, years = 0, months = 0, weeks = 0, days = 0, hours = 0, minutes = 0, seconds = 0] = match

  return Object.entries({
    years,
    months,
    weeks,
    days,
    hours,
    minutes,
    seconds,
  }).reduce((prev, [key, value]) => {
    return { ...prev, [key]: Number(value) }
  }, {} as Required<Duration>)
}

export const parseISODurationToMs = (isoDuration: string) => {
  const duration = parseISODuration(isoDuration)
  return (
    (((duration.days * 24 + duration.hours) * 60 + duration.minutes) * 60 + duration.seconds) * 1000
  )
}

export const parseISODurationToTimeUnitAndFrame = (isoDuration: string) => {
  const duration = parseISODuration(isoDuration)

  const timeUnit = Object.keys(duration).find(key => Boolean(duration[key as keyof Duration])) as
    | keyof Duration
    | undefined

  if (!timeUnit) {
    return { timeFrame: 0, timeUnit: 'minutes' }
  }

  return { timeFrame: duration[timeUnit], timeUnit }
}

export const nextMonday = (day: number | Date = new Date()) => {
  return nextMondayFns(day)
}

export const isSunday = (day: number | Date) => {
  return isSundayFns(day)
}

export const isWeekend = (day: number | Date) => {
  return isWeekendFns(day)
}

export const getHours = (day: number | Date) => {
  return getHoursFns(day)
}

export const getDaysInMonth = (date: Date) => {
  const lastDay = endOfMonth(date)
  return getDateFns(lastDay)
}

/**
 * Get the number of days in a month of a non-leap year
 *
 * @param month - The month to get the number of days for (1-12)
 */
export const getDaysInNonLeapYearMonth = (month: number) => {
  return getDaysInMonth(new Date(2025 /* any non-leap year will do */, month - 1))
}

type GetDateTimeParams = {
  startDate: string
  endDate: string
}

/**
 * Given a start and end date in date format (yyy-mm-dd), it returns ISO string DateTime
 * (2025-07-10T13:37:36.759Z)
 *
 * Contains a bit of temporary business logic that are to be removed (see TODOs)
 */
export const generateDateTimeRangeFromDateRange = ({ startDate, endDate }: GetDateTimeParams) => {
  const now = new Date()

  const timezonedMidnightEnd = parse(endDate, 'yyyy-MM-dd', Date.now())
  const endOfSelectedEnd = endOfDay(timezonedMidnightEnd)

  // TODO we dont need this anymore since BE endpoints should not throw errors
  // when date is future but instead just allow it.

  /** Make sure that end datetime is not in the future */
  const normalizedEndDateTime = isAfter(endOfSelectedEnd, now) ? now : endOfSelectedEnd

  const timezonedMidnightStart = parse(startDate, 'yyyy-MM-dd', Date.now())

  const dayDifference = differenceInDays(normalizedEndDateTime, timezonedMidnightStart)

  // TODO I wonder if we need this after we remove normalizedEndDateTime since 00:00 to 23:59 will become default.

  // because the end time might not be at end of day subtract to keep the
  // timespan 24 hours * difference in days
  // if the difference is negative, it means that the start date is after the end date
  const normalizedStartDateTime =
    dayDifference > 0
      ? subtract(normalizedEndDateTime, { days: dayDifference })
      : timezonedMidnightStart

  return {
    startDateTime: normalizedStartDateTime,
    endDateTime: normalizedEndDateTime,
  }
}
